/* ============================================================
 * <full-look-bundle> — generalized bundle configurator.
 *
 * One component, two bundle modes:
 *   full_look     primary=frame  secondary=necklace  (+ bracelet add-on)
 *   chain_pendant primary=chain  secondary=pendant   (+ optional extra chain)
 *
 * Two flows over the same accordion:
 *   classic  every section open at once (A/B control)
 *   steps    one section open at a time, then an explicit review section
 *
 * The section's inline script picks the flow (?bundle_flow=steps,
 * ?utm_content=steps, or a sticky sessionStorage choice) before this upgrades.
 *
 * Slots are normalized to primary | secondary | extra | review.
 * All customer-visible copy comes from the JSON config the section emits.
 * ============================================================ */

/* Commit gate: long enough to avoid a broken thumbnail, short enough that the
   selection never feels laggy. The hero holds its outgoing frame separately. */
const FL_COMMIT_TIMEOUT = 500;
const FL_IMAGE_TIMEOUT = 1200;
const FL_HERO_SWAP = 420;
const FL_CONFIRM_FLASH = 900;

class FullLookBundle extends HTMLElement {
  connectedCallback() {
    // Shopify can move or re-run this element; never bind twice.
    if (this._booted) return;
    this._booted = true;

    this.config = this.readConfig();
    this.slots = this.config.slots || [];
    this.slotByKey = {};
    this.slotByModal = {};
    this.slots.forEach((slot) => {
      this.slotByKey[slot.key] = slot;
      this.slotByModal[slot.modal] = slot;
    });

    this.discountPercent = Math.max(0, Math.min(100, Number(this.config.discountPercent) || 0));
    // Fixed totals are authored in the shop currency (USD). Shopify renders
    // product prices in the buyer's presentment currency, so convert the offer
    // target with the same rate Shopify publishes in content_for_header.
    this.shopCurrency = this.currencyCode(this.config.shopCurrency, 'USD');
    this.presentmentCurrency = this.currencyCode(
      this.config.presentmentCurrency,
      this.shopCurrency,
    );
    this.baseFixedTotalCents = Math.max(0, Number(this.config.fixedTotalCents) || 0);
    this.presentmentCurrencyRate = this.resolvePresentmentCurrencyRate();
    this.fixedTotalCents = this.presentmentCurrencyRate
      ? Math.round(this.baseFixedTotalCents * this.presentmentCurrencyRate)
      : 0;
    this.pricingMode =
      this.config.pricingMode === 'fixed_total' &&
      this.baseFixedTotalCents === 9900 &&
      this.fixedTotalCents > 0
        ? 'fixed_total'
        : 'percentage';
    this.bundleName = this.config.bundleName || 'Bundle';
    this.bundleMode = this.config.mode === 'chain_pendant' ? 'chain_pendant' : 'full_look';

    this.selection = { primary: null, secondary: null, extra: null };
    this.invalidSlots = new Set();
    this.extraSkipped = false;
    this.braceletOn = false;
    this.activeIndex = 0;
    this.atcState = 'idle';
    this.mobileHeroKey = null;

    this.activeModal = null;
    this.activeSlotKey = null;
    this.modalOpener = null;
    this.pendingPick = null;
    this._committing = false;
    this._portaled = new Set();
    this._prefetched = new Set();

    this.cacheRefs();
    this.bindEvents();
    this.renderPricingCopy();

    // The inline script in the section resolves the flow before this upgrades,
    // so honour what it decided rather than overwriting it.
    this.flowMode = this.dataset.flow === 'steps' ? 'steps' : 'classic';
    this.dataset.flow = this.flowMode;

    this.renderPrice({ animate: false });
    this.renderReview();
    this.syncAtc();
    this.syncSections({ focus: false });
    this.syncSupportingBlocks();
    this.notifySelection();

    // Reveal synchronously. rAF is throttled in background tabs, which would
    // leave the whole purchase column at opacity 0 until the safety fallback.
    this.dataset.flInit = 'ready';
  }

  disconnectedCallback() {
    if (this._onKey) document.removeEventListener('keydown', this._onKey);
    if (this._onFocusIn) document.removeEventListener('focusin', this._onFocusIn);
    if (this._onClick) document.removeEventListener('click', this._onClick);
    if (this._onPointerOver) document.removeEventListener('pointerover', this._onPointerOver);
    this.setBackgroundInert(false);
    this.releaseScroll();
    // Never leave a portaled modal orphaned on <body>.
    this._portaled?.forEach((modal) => modal.remove());
    this._portaled?.clear();
  }

  /* ---------------------------------------------------------- config + refs */

  readConfig() {
    const node = this.querySelector('[data-fl-config]');
    try {
      return node ? JSON.parse(node.textContent) : {};
    } catch (err) {
      return {};
    }
  }

  cacheRefs() {
    this.sections = [...this.querySelectorAll('[data-fl-step]')];
    this.reviewTotal = this.querySelector('[data-fl-review-total]');
    this.reviewCompare = this.querySelector('[data-fl-review-compare]');
    this.reviewSave = this.querySelector('[data-fl-review-save]');

    this.priceBundle = this.querySelector('[data-fl-price-bundle]');
    this.priceCompare = this.querySelector('[data-fl-price-compare]');
    this.priceSave = this.querySelector('[data-fl-price-save]');
    this.priceNote = this.querySelector('[data-fl-price-note]');
    this.subcopy = this.querySelector('[data-fl-subcopy]');

    this.braceletBox = this.querySelector('[data-fl-bracelet]');
    this.braceletToggle = this.querySelector('[data-fl-bracelet-toggle]');
    this.braceletAddon = this.querySelector('[data-fl-bracelet-addon]');
    this.braceletValue = this.querySelector('[data-fl-bracelet-value]');

    this.atcBtn = this.querySelector('[data-fl-atc]');
    this.atcLabel = this.querySelector('[data-fl-atc-label]');
    this.errorEl = this.querySelector('[data-fl-error]');
    this.statusEl = this.querySelector('[data-fl-status]');
    this.priorityCheckbox = this.querySelector('[data-fl-priority-checkbox]');
    this.priorityBox = this.querySelector('[data-fl-priority-box]');
    this.klarnaRoot = this.querySelector('[data-fl-klarna]');

    this.heroRoot = this.querySelector('[data-fl-hero]');
    this.mobileHeroTabs = this.querySelector('[data-fl-mobile-hero-tabs]');
    this.carousels = {};
    ['preview', 'primary', 'secondary', 'extra'].forEach((key) => {
      const carousel = this.bindCarousel(key);
      if (carousel) this.carousels[key] = carousel;
    });
  }

  /* ------------------------------------------------------------- listeners */

  bindEvents() {
    /*
      Delegated on `document`, not on `this`: an open modal is portaled to
      <body> to escape sticky/transformed ancestors, so it is no longer a
      descendant of this element and its events would never reach a listener
      bound here. handleClick scopes every branch explicitly.
    */
    this._onClick = (event) => this.handleClick(event);
    document.addEventListener('click', this._onClick);

    if (window.matchMedia?.('(hover: hover) and (pointer: fine)').matches) {
      this._onPointerOver = (event) => this.handlePrefetch(event);
      document.addEventListener('pointerover', this._onPointerOver);
    }

    if (this.braceletToggle) {
      this.braceletToggle.addEventListener('change', () => {
        this.braceletOn = this.braceletToggle.checked;
        this.braceletBox?.classList.toggle('is-active', this.braceletOn);
        this.renderPrice();
        this.renderReview();
      });
    }

    this._onKey = (event) => this.handleKeydown(event);
    document.addEventListener('keydown', this._onKey);

    this._onFocusIn = (event) => {
      if (!this.activeModal) return;
      // Postscript can open above the selector, including inside its iframe.
      if (event.target.closest?.('#ps__widget_container')) return;
      const panel = this.activeModal.querySelector('[data-fl-panel]');
      if (panel && !panel.contains(event.target)) {
        event.stopPropagation();
        this.focusFirstIn(panel);
      }
    };
    document.addEventListener('focusin', this._onFocusIn);
  }

  handleClick(event) {
    const target = event.target;

    // Everything inside an open modal, scoped so portaling cannot confuse it.
    const modal = this.activeModal;
    if (modal && modal.contains(target)) {
      if (target.closest('[data-fl-close]')) return this.closeModal();
      if (target.closest('[data-fl-back]')) return this.showStyles(modal, { focus: true });
      const style = target.closest('[data-fl-style]');
      if (style) return this.chooseStyle(modal, style);
      const pick = target.closest('[data-fl-pick]');
      if (pick) return this.chooseDirect(pick);
      const color = target.closest('[data-fl-color]');
      if (color) return this.chooseColor(color);
      const option = target.closest('[data-fl-option]');
      if (option) return this.chooseOption(option);
      if (target.closest('[data-fl-confirm]')) return this.confirmPending();
      return;
    }

    // Outside a modal, only ever act on this component's own DOM.
    if (!this.contains(target)) return;

    const open = target.closest('[data-fl-open]');
    if (open) return this.openModal(open.dataset.flOpen, open);

    const toggle = target.closest('[data-fl-toggle]');
    if (toggle && !toggle.disabled) {
      const section = toggle.closest('[data-fl-step]');
      return this.toggleSection(this.sections.indexOf(section));
    }

    if (target.closest('[data-fl-skip-optional]')) return this.skipOptional();
    if (target.closest('[data-fl-atc]')) return this.addToCart();

    const dot = target.closest('[data-fl-hero-dot]');
    if (dot) return this.goCarousel(dot.dataset.flHeroKey, Number(dot.dataset.flHeroDot));
    const nav = target.closest('[data-fl-hero-nav]');
    if (nav) return this.shiftCarousel(nav.dataset.flHeroKey, Number(nav.dataset.flHeroNav));
    const mobileHero = target.closest('[data-fl-mobile-hero]');
    if (mobileHero) return this.setMobileHero(mobileHero.dataset.flMobileHero);
  }

  handlePrefetch(event) {
    const tile = event.target.closest('[data-images], [data-image]');
    if (!tile) return;
    if (!this.contains(tile) && !this.activeModal?.contains(tile)) return;
    if (tile.dataset.flPrefetched === '1') return;
    tile.dataset.flPrefetched = '1';
    this.prefetchImages(tile.dataset.images || tile.dataset.image);
  }

  handleKeydown(event) {
    if (!this.activeModal) return;
    if (event.target.closest?.('#ps__widget_container')) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeModal();
      return;
    }
    if (event.key !== 'Tab') return;

    const panel = this.activeModal.querySelector('[data-fl-panel]');
    if (!panel) return;
    const focusables = this.focusablesIn(panel);
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && (active === first || !panel.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* ------------------------------------------------------------- utilities */

  parseJson(raw, fallback) {
    try {
      return raw ? JSON.parse(raw) : fallback;
    } catch (err) {
      return fallback;
    }
  }

  currencyCode(raw, fallback) {
    const code = String(raw || '').trim().toUpperCase();
    return /^[A-Z]{3}$/.test(code) ? code : fallback;
  }

  resolvePresentmentCurrencyRate() {
    if (this.presentmentCurrency === this.shopCurrency) return 1;
    const rate = Number(window.Shopify?.currency?.rate);
    return Number.isFinite(rate) && rate > 0 ? rate : 0;
  }

  formatMoney(cents, currency = this.presentmentCurrency) {
    const dollars = (Number(cents) || 0) / 100;
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: dollars % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(dollars);
  }

  renderPricingCopy() {
    if (!this.subcopy || this.config.pricingMode !== 'fixed_total') return;
    const usdPrice = `USD ${this.formatMoney(this.baseFixedTotalCents, this.shopCurrency)}`;
    if (this.pricingMode !== 'fixed_total') {
      this.subcopy.textContent =
        `Choose your chain. Add a pendant. Get the set for ${usdPrice} total; ` +
        'the local conversion is confirmed at checkout.';
      return;
    }
    const target = this.formatMoney(this.fixedTotalCents);
    this.subcopy.textContent =
      this.presentmentCurrency === this.shopCurrency
        ? `Choose your chain. Add a pendant. Get the set for ${target} total.`
        : `Choose your chain. Add a pendant. Get the set for ${target} total (${usdPrice}).`;
  }

  imageList(raw) {
    if (Array.isArray(raw)) return raw.filter(Boolean);
    return this.parseJson(raw, []).filter(Boolean);
  }

  prefetchImages(raw) {
    this.imageList(raw)
      .slice(0, 2)
      .forEach((src) => {
        if (!src || this._prefetched.has(src)) return;
        this._prefetched.add(src);
        const img = new Image();
        img.decoding = 'async';
        img.src = src;
      });
  }

  /**
   * Resolves 'ok' | 'error' | 'timeout'.
   * 'timeout' is deliberately distinct from 'error': a slow image is still a
   * good image, and must not be stripped from the selection.
   */
  loadImage(src, timeout = FL_IMAGE_TIMEOUT) {
    if (!src) return Promise.resolve('error');
    return new Promise((resolve) => {
      let settled = false;
      const done = (status) => {
        if (settled) return;
        settled = true;
        resolve(status);
      };
      const img = new Image();
      img.decoding = 'async';
      img.onload = () => {
        if (typeof img.decode === 'function') img.decode().then(() => done('ok'), () => done('ok'));
        else done('ok');
      };
      img.onerror = () => done('error');
      img.src = src;
      if (img.complete && img.naturalWidth) done('ok');
      setTimeout(() => done(img.naturalWidth ? 'ok' : 'timeout'), timeout);
    });
  }

  focusablesIn(root) {
    return [
      ...root.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    ].filter((el) => el.offsetParent !== null || el === document.activeElement);
  }

  focusFirstIn(panel) {
    const heading = panel.querySelector('[data-fl-modal-title]');
    if (heading) {
      heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: true });
      return;
    }
    this.focusablesIn(panel)[0]?.focus({ preventScroll: true });
  }

  /** One concise message per commit — never one per DOM node touched. */
  announce(message) {
    if (!this.statusEl || !message) return;
    this.statusEl.textContent = message;
  }

  prefersReducedMotion() {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
  }

  /* ------------------------------------------------------------ scroll lock */

  lockScroll() {
    if (this._scrollLocked) return;
    this._scrollY = window.scrollY;
    this._scrollLocked = true;
    const body = document.body;
    body.style.position = 'fixed';
    body.style.top = `-${this._scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.classList.add('overflow-hidden');
  }

  releaseScroll() {
    if (!this._scrollLocked) return;
    this._scrollLocked = false;
    const body = document.body;
    body.style.position = '';
    body.style.top = '';
    body.style.left = '';
    body.style.right = '';
    body.style.width = '';
    body.classList.remove('overflow-hidden');
    window.scrollTo(0, this._scrollY || 0);
  }

  /* ------------------------------------------------------------------ modal */

  findModal(kind) {
    // Only ever adopt a modal this instance owns (in-tree, or portaled by us).
    const own = this.querySelector(`[data-fl-modal="${kind}"]`);
    if (own) return own;
    for (const modal of this._portaled) {
      if (modal.dataset.flModal === kind) return modal;
    }
    return null;
  }

  openModal(kind, opener) {
    const modal = this.findModal(kind);
    if (!modal) return;
    if (this.activeModal && this.activeModal !== modal) this.closeModal({ restoreFocus: false });

    this.modalOpener = opener || document.activeElement;
    this.activeModal = modal;
    this.activeSlotKey = this.slotByModal[kind]?.key || null;
    this.pendingPick = null;

    if (modal.parentElement !== document.body) {
      // Keep the restore point ON the modal. Holding it on the instance meant
      // opening a second modal overwrote the first one's home, and the delayed
      // restore then stranded a modal on <body> for good.
      modal._flHome = { parent: modal.parentElement, next: modal.nextSibling };
      document.body.appendChild(modal);
      this._portaled.add(modal);
    }

    this.showStyles(modal, { focus: false });
    modal.hidden = false;
    this.lockScroll();
    this.setBackgroundInert(true);

    // Force a reflow so the entrance transition has a start frame. rAF would be
    // throttled in a background tab and could leave the panel stuck at opacity 0.
    void modal.offsetWidth;
    modal.classList.add('is-open');
    const panel = modal.querySelector('[data-fl-panel]');
    if (panel) this.focusFirstIn(panel);
  }

  closeModal({ restoreFocus = true } = {}) {
    const modal = this.activeModal;
    if (!modal) return;
    const opener = this.modalOpener;

    this.activeModal = null;
    this.activeSlotKey = null;
    this.pendingPick = null;
    this.modalOpener = null;

    modal.classList.remove('is-open');
    this.setBackgroundInert(false, modal);
    this.releaseScroll();

    const finish = () => {
      modal.hidden = true;
      const home = modal._flHome;
      if (home?.parent) {
        if (home.next && home.next.parentNode === home.parent) home.parent.insertBefore(modal, home.next);
        else home.parent.appendChild(modal);
        this._portaled.delete(modal);
        modal._flHome = null;
      }
    };

    if (this.prefersReducedMotion()) finish();
    else setTimeout(finish, 220);

    if (restoreFocus && opener && document.contains(opener)) {
      opener.focus({ preventScroll: true });
    }
  }

  /** Keeps the background out of the tab order while a dialog owns the screen. */
  setBackgroundInert(on, modalOverride) {
    const modal = modalOverride || this.activeModal;
    [...document.body.children].forEach((child) => {
      if (child === modal || child.tagName === 'SCRIPT' || child.tagName === 'STYLE') return;
      // The offer popup owns its own focus and close button above our dialog.
      if (child.id === 'ps__widget_container') return;
      if (on) {
        child.setAttribute('inert', '');
        child.setAttribute('aria-hidden', 'true');
        child.dataset.flInerted = '1';
      } else if (child.dataset.flInerted) {
        child.removeAttribute('inert');
        child.removeAttribute('aria-hidden');
        delete child.dataset.flInerted;
      }
    });
  }

  /* ------------------------------------------------------------ modal panes */

  setModalHead(modal, { kicker, title, showBack }) {
    const kickerEl = modal.querySelector('[data-fl-modal-kicker]');
    const titleEl = modal.querySelector('[data-fl-modal-title]');
    const backEl = modal.querySelector('[data-fl-back]');
    if (kickerEl) kickerEl.textContent = kicker || '';
    if (titleEl) titleEl.textContent = title || '';
    if (backEl) backEl.hidden = !showBack;
  }

  setModalFooter(modal, { label, visible, enabled }) {
    const foot = modal.querySelector('[data-fl-foot]');
    const confirm = modal.querySelector('[data-fl-confirm]');
    if (foot) foot.hidden = !visible;
    if (confirm) {
      if (label) confirm.textContent = label;
      confirm.disabled = !enabled;
      confirm.setAttribute('aria-disabled', String(!enabled));
    }
  }

  showStyles(modal, { focus } = {}) {
    const styles = modal.querySelector('[data-fl-pane="styles"]');
    modal.querySelectorAll('[data-fl-pane="colors"]').forEach((pane) => {
      pane.hidden = true;
    });
    if (styles) styles.hidden = false;
    this.pendingPick = null;

    this.setModalHead(modal, {
      kicker: modal.dataset.flKicker || '',
      title: modal.dataset.flStyleTitle || 'Select style',
      showBack: false,
    });
    this.setModalFooter(modal, { visible: false, enabled: false });

    const body = modal.querySelector('[data-fl-modal-body]');
    if (body) body.scrollTop = 0;
    if (focus) {
      const panel = modal.querySelector('[data-fl-panel]');
      if (panel) this.focusFirstIn(panel);
    }
  }

  paneFor(modal, key) {
    if (!modal || !key) return null;
    return modal.querySelector(
      `[data-fl-pane="colors"][data-style-key="${String(key).replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"]`,
    );
  }

  chooseStyle(modal, styleBtn) {
    if (this._committing) return;
    const pane = this.paneFor(modal, styleBtn.dataset.styleKey);
    if (!pane) return;

    modal.querySelectorAll('[data-fl-style]').forEach((btn) => {
      btn.classList.remove('is-selected');
      btn.setAttribute('aria-pressed', 'false');
    });
    styleBtn.classList.add('is-selected');
    styleBtn.setAttribute('aria-pressed', 'true');

    const needsFinish = pane.dataset.needsFinish === 'true';
    const needsLength = pane.dataset.needsLength === 'true';

    // One finish, one variant: there is nothing left to ask.
    if (pane.dataset.flPaneKind === 'options' && !needsFinish && !needsLength) {
      this.seedOptions(pane);
      if (this.pendingPick?.variantId) {
        this.commit(this.pendingPick, styleBtn);
        return;
      }
    }

    modal.querySelector('[data-fl-pane="styles"]').hidden = true;
    modal.querySelectorAll('[data-fl-pane="colors"]').forEach((p) => {
      p.hidden = true;
    });
    pane.hidden = false;

    this.setModalHead(modal, {
      kicker: pane.dataset.paneKicker || '',
      title: pane.dataset.paneTitle || 'Select options',
      showBack: true,
    });

    if (pane.dataset.flPaneKind === 'options') {
      if (needsFinish) {
        const preferredFinish = this.preferredFinish(pane);
        if (preferredFinish) this.selectColor(preferredFinish, { commitSingle: false });
        else this.setModalFooter(modal, { visible: false, enabled: false });
      } else {
        this.seedOptions(pane);
      }
    } else {
      this.setModalFooter(modal, { visible: false, enabled: false });
    }

    const body = modal.querySelector('[data-fl-modal-body]');
    if (body) body.scrollTop = 0;
    const panel = modal.querySelector('[data-fl-panel]');
    if (panel) this.focusFirstIn(panel);
  }

  /** Direct pick (Full Look frames): one button carries the whole variant. */
  chooseDirect(btn) {
    if (btn.disabled || this._committing) return;
    const pane = btn.closest('[data-fl-pane="colors"]');
    if (pane) {
      pane.querySelectorAll('[data-fl-pick]').forEach((el) => {
        el.classList.remove('is-selected');
        el.setAttribute('aria-pressed', 'false');
      });
      btn.classList.add('is-selected');
      btn.setAttribute('aria-pressed', 'true');
    }
    const images = this.imageList(btn.dataset.images);
    this.commit(
      {
        slot: this.activeSlotKey,
        productId: btn.dataset.productId,
        variantId: String(btn.dataset.variantId || ''),
        title: btn.dataset.title || '',
        colorLabel: btn.dataset.colorLabel || '',
        variantLabel: '',
        price: Number(btn.dataset.price) || 0,
        compareAt: Number(btn.dataset.compare) || 0,
        available: btn.dataset.available !== 'false',
        image: btn.dataset.image || images[0] || '',
        images: images.length ? images : btn.dataset.image ? [btn.dataset.image] : [],
        bracelet: null,
      },
      btn,
    );
  }

  /** Finish/colorway pick inside a jewelry options pane. */
  chooseColor(btn) {
    this.selectColor(btn);
  }

  /** Gold leads when available, while every finish remains visibly selectable. */
  preferredFinish(pane) {
    const finishes = [...pane.querySelectorAll('[data-fl-color]:not(:disabled)')];
    return (
      finishes.find((btn) => {
        const finish = String(btn.dataset.finish || '').trim();
        return finish.includes('gold') && !finish.includes('rose');
      }) || finishes[0]
    );
  }

  selectColor(btn, { commitSingle = true } = {}) {
    if (btn.disabled || this._committing) return;
    const pane = btn.closest('[data-fl-pane="colors"]');
    if (!pane) return;
    pane.querySelectorAll('[data-fl-color]').forEach((el) => {
      el.classList.remove('is-selected');
      el.setAttribute('aria-pressed', 'false');
    });
    btn.classList.add('is-selected');
    btn.setAttribute('aria-pressed', 'true');
    this.seedOptions(pane, btn, { commitSingle });
  }

  /**
   * Build the option row for a pane and stage a *pending* pick.
   * Nothing here touches committed state — that only happens in commit().
   * (The old build reused the committed pendant id as scratch space, which
   * silently rewrote a confirmed pendant to whatever chain was being browsed.)
   */
  seedOptions(pane, fromBtn, { commitSingle = true } = {}) {
    const wrap = pane.querySelector('[data-fl-options]');
    const modal = pane.closest('[data-fl-modal]');
    if (!wrap) return;

    const source = fromBtn ? fromBtn.dataset : wrap.dataset;
    const prefix = fromBtn ? '' : 'default';
    const read = (name) => source[prefix ? prefix + name.charAt(0).toUpperCase() + name.slice(1) : name];

    const images = this.imageList(read('images'));
    const variants = (this.parseJson(read('variants'), []) || []).filter((v) => v && v.id);
    const bracelet = this.parseJson(read('bracelet'), null);

    this.pendingPick = {
      slot: this.activeSlotKey,
      productId: read('productId'),
      title: read('title') || '',
      colorLabel: read('colorLabel') || '',
      variantLabel: '',
      price: Number(read('price')) || 0,
      compareAt: 0,
      available: read('available') !== 'false',
      image: read('image') || images[0] || '',
      images: images.length ? images : read('image') ? [read('image')] : [],
      bracelet,
      variantId: null,
    };

    wrap.innerHTML = '';
    const optionTitle = pane.querySelector('[data-fl-option-title]');
    const multi = variants.length > 1;

    if (!multi) {
      wrap.hidden = true;
      if (optionTitle) optionTitle.hidden = true;
      if (variants.length === 1) {
        this.pendingPick.variantId = String(variants[0].id);
        this.pendingPick.variantLabel = this.optionLabel(variants[0]);
        if (typeof variants[0].price === 'number') this.pendingPick.price = variants[0].price;
        this.pendingPick.available = variants[0].available !== false;
      }
      // A finish was just chosen and nothing else is left to ask — commit now.
      if (fromBtn && commitSingle && this.pendingPick.variantId) {
        this.commit(this.pendingPick, fromBtn);
        return;
      }
      if (modal) {
        this.setModalFooter(modal, {
          label: modal.dataset.flConfirm,
          visible: pane.dataset.needsFinish === 'true',
          enabled: Boolean(this.pendingPick.variantId),
        });
      }
      return;
    }

    wrap.hidden = false;
    if (optionTitle) optionTitle.hidden = false;

    variants.forEach((variant, index) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'full-look__option';
      btn.textContent = this.optionLabel(variant);
      btn.dataset.flOption = '';
      btn.dataset.variantId = String(variant.id);
      btn.dataset.price = String(variant.price ?? '');
      btn.setAttribute('aria-pressed', 'false');
      if (variant.available === false) {
        btn.disabled = true;
        btn.setAttribute('aria-disabled', 'true');
        btn.title = 'Sold out';
      }
      wrap.appendChild(btn);
      const firstUsable = !this.pendingPick.variantId && variant.available !== false;
      if (firstUsable || (index === variants.length - 1 && !this.pendingPick.variantId)) {
        this.chooseOption(btn, { silent: true });
      }
    });

    if (modal) {
      this.setModalFooter(modal, {
        label: modal.dataset.flConfirm,
        visible: true,
        enabled: Boolean(this.pendingPick.variantId),
      });
    }
  }

  optionLabel(variant) {
    const raw = variant.title || variant.option1 || '';
    return raw === 'Default Title' ? 'One size' : raw;
  }

  chooseOption(btn, { silent } = {}) {
    if (btn.disabled || !this.pendingPick) return;
    btn.parentElement?.querySelectorAll('[data-fl-option]').forEach((el) => {
      el.classList.remove('is-selected');
      el.setAttribute('aria-pressed', 'false');
    });
    btn.classList.add('is-selected');
    btn.setAttribute('aria-pressed', 'true');
    this.pendingPick.variantId = String(btn.dataset.variantId);
    this.pendingPick.variantLabel = btn.textContent;
    const price = Number(btn.dataset.price);
    if (!Number.isNaN(price) && price > 0) this.pendingPick.price = price;

    if (this.activeModal && !silent) {
      this.setModalFooter(this.activeModal, {
        label: this.activeModal.dataset.flConfirm,
        visible: true,
        enabled: true,
      });
    }
  }

  confirmPending() {
    if (!this.pendingPick?.variantId || this._committing) return;
    this.commit(this.pendingPick, this.activeModal?.querySelector('[data-fl-confirm]'));
  }

  /* ---------------------------------------------------- selection lifecycle */

  async commit(item, sourceEl) {
    if (this._committing) return;
    if (!item?.slot || !item.variantId) return;
    this._committing = true;

    const modal = this.activeModal;
    const slotKey = item.slot;
    sourceEl?.classList.add('is-committing');
    modal?.setAttribute('aria-busy', 'true');

    // Only a genuine load failure drops the asset — a slow one is still valid.
    if ((await this.loadImage(item.image, FL_COMMIT_TIMEOUT)) === 'error' && item.image) {
      item.images = item.images.filter((src) => src !== item.image);
      item.image = item.images[0] || '';
    }

    this.selection[slotKey] = item;
    this.invalidSlots.delete(slotKey);
    if (slotKey === 'extra') this.extraSkipped = false;
    if (slotKey === 'secondary') this.renderBracelet(item.bracelet);

    this.renderSlot(slotKey, { confirm: true });
    this.setHero(slotKey, item);
    this.renderPrice();
    this.renderReview();
    this.syncAtc();
    this.notifySelection();

    const label = [item.title, item.colorLabel, item.variantLabel]
      .filter((part) => part && part !== 'One size')
      .join(', ');
    const totals = this.totals();
    this.announce(
      `${label || this.slotByKey[slotKey]?.label || 'Item'} selected.` +
        (totals.hasSelection ? ` Bundle total ${this.formatMoney(totals.bundle)}.` : ''),
    );

    sourceEl?.classList.remove('is-committing');
    modal?.removeAttribute('aria-busy');
    this._committing = false;

    this.closeModal();
    // Confirming always resolves to the next outstanding section — including
    // when the customer edited from Review, which simply lands back on Review.
    if (this.flowMode === 'steps') this.advance();
    else this.syncSections({ focus: false });
    this.syncSupportingBlocks();
  }

  /* ------------------------------------------------------- accordion sections */

  sectionIndex(slotKey) {
    return this.sections.findIndex((section) => section.dataset.flSlot === slotKey);
  }

  sectionFor(slotKey) {
    return this.querySelector(`[data-fl-slot="${slotKey}"]`);
  }

  /** Highest section index the customer has legitimately unlocked. */
  furthestIndex() {
    let reached = 0;
    for (let i = 1; i < this.sections.length; i += 1) {
      if (!this.canLeave(i - 1)) break;
      reached = i;
    }
    return reached;
  }

  canLeave(index) {
    const slotKey = this.sections[index]?.dataset.flSlot;
    if (!slotKey || slotKey === 'review') return false;
    const slot = this.slotByKey[slotKey];
    if (slot?.optional) return Boolean(this.selection[slotKey] || this.extraSkipped);
    return Boolean(this.selection[slotKey]?.variantId);
  }

  toggleSection(index) {
    if (index < 0) return;
    if (this.flowMode === 'steps' && index > this.furthestIndex()) return;
    this.activeIndex = this.activeIndex === index && this.flowMode === 'classic' ? -1 : index;
    this.syncSections();
  }

  /** First section still awaiting a decision; Review is the terminal target. */
  nextIncompleteIndex() {
    for (let i = 0; i < this.sections.length; i += 1) {
      const key = this.sections[i].dataset.flSlot;
      if (key === 'review') return i;
      const slot = this.slotByKey[key];
      const resolved = slot?.optional
        ? Boolean(this.selection[key] || this.extraSkipped)
        : Boolean(this.selection[key]?.variantId);
      if (!resolved) return i;
    }
    return this.sections.length - 1;
  }

  /*
    The accordion headers are the only navigation, so confirming has to land
    the customer somewhere useful: the next unanswered section, or Review once
    everything is answered. Searching from the top (not from activeIndex + 1)
    means re-editing an early section returns to whatever is still outstanding
    rather than walking back through choices already made.
  */
  advance() {
    this.activeIndex = Math.min(this.nextIncompleteIndex(), this.furthestIndex());
    this.syncSections();
  }

  syncSections({ focus = true } = {}) {
    const stepped = this.flowMode === 'steps';
    const furthest = this.furthestIndex();

    this.sections.forEach((section, i) => {
      const slotKey = section.dataset.flSlot;
      const isReview = slotKey === 'review';
      const filled = Boolean(this.selection[slotKey]);
      const skipped = this.slotByKey[slotKey]?.optional && this.extraSkipped;
      // Classic keeps every section open; stepped opens exactly one — except
      // Review, which stays open throughout. The CTA lives inside Review, and
      // the last slot is optional, so `advance()` legitimately parks on it: a
      // collapsed Review clipped the button to zero height and left a
      // purchasable set with no way to buy. Keeping it open also means the CTA
      // is present from the start, reading "Choose your …" until it is ready.
      const expanded = stepped ? i === this.activeIndex || isReview : true;
      const locked = stepped && i > furthest;

      section.classList.toggle('is-open', expanded);
      section.classList.toggle('is-filled', filled);
      section.classList.toggle('is-skipped', Boolean(skipped && !filled));
      section.classList.toggle('is-locked', locked);
      section.classList.toggle('is-invalid', this.invalidSlots.has(slotKey));

      // A filled required slot has nothing left to expand — its card and Edit
      // are always on screen — so don't advertise a control that does nothing.
      const inert = filled && !this.slotByKey[slotKey]?.optional && !isReview;
      const toggle = section.querySelector('[data-fl-toggle]');
      if (toggle) {
        if (inert) {
          toggle.removeAttribute('aria-expanded');
          toggle.setAttribute('aria-disabled', 'true');
        } else {
          toggle.setAttribute('aria-expanded', String(expanded));
          toggle.removeAttribute('aria-disabled');
        }
        toggle.disabled = locked;
        if (stepped && expanded) toggle.setAttribute('aria-current', 'step');
        else toggle.removeAttribute('aria-current');
      }
      const panel = section.querySelector('[data-fl-slot-panel]');
      if (panel) panel.setAttribute('aria-hidden', String(!expanded));

      if (isReview) {
        // Review carries the CTA, so it is always present — hiding it until the
        // customer "reached" it stranded anyone who left the optional slot alone.
        section.hidden = false;
        if (expanded) this.renderReview();
      }
    });

    // Only ever one total on screen: the running price while building, the
    // Review total once the customer is confirming.
    const onReview = stepped && this.sections[this.activeIndex]?.dataset.flSlot === 'review';
    this.classList.toggle('is-reviewing', Boolean(onReview));

    this.syncAtc();
    this.syncSupportingBlocks();

    if (focus && stepped) {
      const toggle = this.sections[this.activeIndex]?.querySelector('[data-fl-toggle]');
      toggle?.focus({ preventScroll: true });
    }
  }

  skipOptional() {
    const section = this.sections[this.activeIndex] || this.sectionFor('extra');
    const slotKey = section?.dataset.flSlot;
    if (!slotKey || !this.slotByKey[slotKey]?.optional) return;
    // Skipping must leave no residue behind.
    this.clearSlot(slotKey);
    this.extraSkipped = true;
    this.renderPrice();
    this.renderReview();
    this.announce(`${this.slotByKey[slotKey].label} skipped.`);
    this.notifySelection();
    if (this.flowMode === 'steps') this.advance();
    else this.syncSections({ focus: false });
  }

  /* ------------------------------------------------------------ slot summary */

  renderSlot(slotKey, { confirm } = {}) {
    const section = this.sectionFor(slotKey);
    if (!section) return;
    const item = this.selection[slotKey];
    const slot = this.slotByKey[slotKey];

    const card = section.querySelector('[data-fl-card]');
    const media = section.querySelector('[data-fl-card-media]');
    const img = section.querySelector('[data-fl-card-img]');
    const choose = section.querySelector('[data-fl-choose]');
    const chooseLabel = section.querySelector('[data-fl-choose-label]');
    const set = (sel, value) => {
      const el = section.querySelector(sel);
      if (!el) return;
      el.textContent = value || '';
      el.hidden = !value;
    };

    if (!item) {
      if (card) card.hidden = true;
      if (img) img.removeAttribute('src');
      if (chooseLabel && choose) {
        chooseLabel.textContent =
          this.extraSkipped && slot?.optional ? 'Skipped — add one' : choose.dataset.labelEmpty || '';
      }
      return;
    }

    if (img && media) {
      if (item.image) {
        img.src = item.image;
        img.alt = [item.title, item.colorLabel].filter(Boolean).join(' — ');
        img.onerror = () => {
          media.classList.add('is-missing');
          img.removeAttribute('src');
        };
        media.classList.remove('is-missing');
        media.hidden = false;
      } else {
        media.hidden = true;
      }
    }

    set('[data-fl-card-title]', item.title || slot?.label || '');
    set(
      '[data-fl-card-meta]',
      [item.colorLabel, item.variantLabel && item.variantLabel !== 'One size' ? item.variantLabel : '']
        .filter(Boolean)
        .join(' · '),
    );

    const full = Number(item.price) || 0;
    const sale = this.discounted(full);
    const strike = sale < full ? full : Number(item.compareAt) > sale ? Number(item.compareAt) : 0;
    set('[data-fl-card-price]', this.formatMoney(sale));
    set('[data-fl-card-compare]', strike > sale ? this.formatMoney(strike) : '');
    // Only ever claims a saving the checkout will honour (discount_active).
    set(
      '[data-fl-card-save]',
      sale < full && this.discountPercent > 0
        ? `${this.discountPercent}% off with ${this.bundleName}`
        : '',
    );

    if (card) card.hidden = false;
    if (chooseLabel && choose) chooseLabel.textContent = choose.dataset.labelChange || 'Change selection';

    if (confirm) {
      card?.classList.add('is-confirming');
      clearTimeout(section._flConfirmTimer);
      section._flConfirmTimer = setTimeout(
        () => card?.classList.remove('is-confirming'),
        FL_CONFIRM_FLASH,
      );
    }
  }

  clearSlot(slotKey) {
    this.selection[slotKey] = null;
    this.invalidSlots.delete(slotKey);
    this.renderSlot(slotKey);
    this.clearHero(slotKey);
  }

  /* -------------------------------------------------------------- hero stage */

  bindCarousel(key) {
    const root = this.querySelector(`[data-fl-hero-carousel="${key}"]`);
    if (!root) return null;
    return {
      key,
      root,
      layers: [...root.querySelectorAll('[data-fl-hero-layer]')],
      active: 0,
      empty: root.querySelector('[data-fl-hero-empty]'),
      prev: root.querySelector('[data-fl-hero-nav="-1"]'),
      next: root.querySelector('[data-fl-hero-nav="1"]'),
      dots: root.querySelector('[data-fl-hero-dots]'),
      images: [],
      index: 0,
      alt: '',
    };
  }

  setHero(slotKey, item) {
    if (!this.slotByKey[slotKey]?.hero) return;
    this.mobileHeroKey = slotKey;
    this.setCarouselImages(
      slotKey,
      item.images,
      [item.title, item.colorLabel].filter(Boolean).join(' — '),
    );
    this.syncHeroComposition();
  }

  clearHero(slotKey) {
    const carousel = this.carousels[slotKey];
    if (!carousel) return;
    carousel.images = [];
    carousel.index = 0;
    carousel.layers.forEach((layer, i) => {
      layer.innerHTML = '';
      layer.classList.toggle('is-active', i === 0);
    });
    carousel.active = 0;
    if (carousel.empty) carousel.empty.hidden = false;
    if (carousel.prev) carousel.prev.hidden = true;
    if (carousel.next) carousel.next.hidden = true;
    if (carousel.dots) {
      carousel.dots.hidden = true;
      carousel.dots.innerHTML = '';
    }
    this.syncHeroComposition();
  }

  syncHeroComposition() {
    if (!this.heroRoot) return;
    const filled = ['primary', 'secondary', 'extra'].filter(
      (key) => this.slotByKey[key]?.hero && this.selection[key],
    );
    this.heroRoot.dataset.flComposition = ['empty', 'one', 'two', 'three'][Math.min(filled.length, 3)];
    // Media stacks in DOM order, full width — no lead/companion promotion.

    ['primary', 'secondary', 'extra'].forEach((key) => {
      const frame = this.querySelector(`[data-fl-hero-frame="${key}"]`);
      if (!frame) return;
      frame.hidden = Boolean(this.slotByKey[key]?.optional) && !this.selection[key];
    });
    const preview = this.querySelector('[data-fl-hero-frame="preview"]');
    if (preview) preview.hidden = filled.length > 0;
    if (!filled.includes(this.mobileHeroKey)) this.mobileHeroKey = filled[0] || null;
    this.renderMobileHeroTabs(filled);
    this.setMobileHero(this.mobileHeroKey);
  }

  renderMobileHeroTabs(filled) {
    if (!this.mobileHeroTabs) return;
    this.mobileHeroTabs.replaceChildren();
    this.mobileHeroTabs.hidden = filled.length === 0;
    if (!filled.length) return;

    const fragment = document.createDocumentFragment();
    filled.forEach((key) => {
      const item = this.selection[key];
      const slot = this.slotByKey[key];
      if (!item || !slot) return;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'full-look__hero-thumb';
      button.dataset.flMobileHero = key;
      button.setAttribute('aria-label', `Show ${slot.label}: ${item.title || 'selected product'}`);

      const media = document.createElement('span');
      media.className = 'full-look__hero-thumb-media';
      if (item.image) {
        const image = document.createElement('img');
        image.src = item.image;
        image.alt = '';
        image.width = 88;
        image.height = 88;
        image.loading = 'lazy';
        image.decoding = 'async';
        media.append(image);
      } else {
        media.classList.add('is-missing');
      }

      const label = document.createElement('span');
      label.className = 'full-look__hero-thumb-label';
      label.textContent = slot.label;
      button.append(media, label);
      fragment.append(button);
    });
    this.mobileHeroTabs.append(fragment);
  }

  setMobileHero(key) {
    if (!this.heroRoot) return;
    const selected = Boolean(key && this.selection[key] && this.slotByKey[key]?.hero);
    if (selected) this.mobileHeroKey = key;

    ['primary', 'secondary', 'extra'].forEach((slotKey) => {
      const frame = this.querySelector(`[data-fl-hero-frame="${slotKey}"]`);
      frame?.classList.toggle('is-mobile-active', selected && slotKey === key);
    });
    this.mobileHeroTabs?.querySelectorAll('[data-fl-mobile-hero]').forEach((button) => {
      const active = selected && button.dataset.flMobileHero === key;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  async setCarouselImages(key, images, alt) {
    const carousel = this.carousels[key];
    if (!carousel) return;
    const list = this.imageList(images);
    if (!list.length) {
      this.clearHero(key);
      return;
    }

    const same = carousel.images.length === list.length && carousel.images.every((src, i) => src === list[i]);
    if (same) {
      this.goCarousel(key, 0);
      return;
    }

    const incoming = carousel.layers[(carousel.active + 1) % carousel.layers.length];
    const outgoing = carousel.layers[carousel.active];
    const safeAlt = (alt || '').replace(/"/g, '&quot;');

    incoming.innerHTML = `<div class="full-look__hero-track">${list
      .map(
        (src, i) =>
          `<div class="full-look__hero-slide${i === 0 ? ' is-active' : ''}">
            <img class="full-look__hero-img" src="${src}" alt="${i === 0 ? safeAlt : ''}" loading="${
              i === 0 ? 'eager' : 'lazy'
            }" decoding="async">
          </div>`,
      )
      .join('')}</div>`;

    // Hold the outgoing frame until the incoming primary image is actually ready.
    await this.loadImage(list[0]);

    carousel.images = list;
    carousel.index = 0;
    carousel.alt = alt || '';
    carousel.active = carousel.layers.indexOf(incoming);
    incoming.classList.add('is-active');
    outgoing.classList.remove('is-active');
    if (carousel.empty) carousel.empty.hidden = true;

    const clear = () => {
      if (!outgoing.classList.contains('is-active')) outgoing.innerHTML = '';
    };
    if (this.prefersReducedMotion()) clear();
    else setTimeout(clear, FL_HERO_SWAP);

    const multi = list.length > 1;
    if (carousel.prev) carousel.prev.hidden = !multi;
    if (carousel.next) carousel.next.hidden = !multi;
    if (carousel.dots) {
      carousel.dots.hidden = !multi;
      carousel.dots.innerHTML = multi
        ? list
            .map(
              (_, i) =>
                `<button type="button" class="full-look__hero-dot${i === 0 ? ' is-active' : ''}" data-fl-hero-dot="${i}" data-fl-hero-key="${key}" aria-label="Image ${i + 1} of ${list.length}"></button>`,
            )
            .join('')
        : '';
    }
    // Warm the next image only — never the whole gallery.
    if (list[1]) this.prefetchImages([list[1]]);
  }

  activeTrack(carousel) {
    return carousel.layers[carousel.active]?.querySelector('.full-look__hero-track');
  }

  shiftCarousel(key, delta) {
    const carousel = this.carousels[key];
    if (!carousel || !carousel.images.length) return;
    this.goCarousel(key, (carousel.index + delta + carousel.images.length) % carousel.images.length);
  }

  goCarousel(key, index) {
    const carousel = this.carousels[key];
    const track = carousel && this.activeTrack(carousel);
    if (!track || !carousel.images.length) return;
    carousel.index = index;
    track.querySelectorAll('.full-look__hero-slide').forEach((slide, i) => {
      slide.classList.toggle('is-active', i === index);
      const img = slide.querySelector('img');
      if (img && i === index && !img.alt) img.alt = carousel.alt;
    });
    carousel.dots?.querySelectorAll('.full-look__hero-dot').forEach((dot, i) => {
      dot.classList.toggle('is-active', i === index);
      dot.setAttribute('aria-current', i === index ? 'true' : 'false');
    });
  }

  /* ------------------------------------------------------------------ pricing */

  discounted(cents) {
    if (!this.discountPercent) return cents;
    return Math.round(cents * (1 - this.discountPercent / 100));
  }

  totals() {
    let compare = 0;
    let bundle = 0;
    let requiredCompare = 0;
    let extraCompare = 0;
    ['primary', 'secondary', 'extra'].forEach((key) => {
      const item = this.selection[key];
      if (!item) return;
      const price = Number(item.price) || 0;
      compare += price;
      if (key === 'extra') extraCompare += price;
      else requiredCompare += price;
    });

    if (this.pricingMode === 'fixed_total') {
      // The fixed price only exists once both required pieces form a set. If a
      // stale extra selection somehow survives, it remains at full price.
      bundle =
        this.selection.primary && this.selection.secondary
          ? Math.min(requiredCompare, this.fixedTotalCents) + extraCompare
          : requiredCompare + extraCompare;
    } else {
      bundle = ['primary', 'secondary', 'extra'].reduce((total, key) => {
        const price = Number(this.selection[key]?.price) || 0;
        return total + this.discounted(price);
      }, 0);
    }

    if (this.braceletOn && this.selection.secondary?.bracelet) {
      const bracelet = this.selection.secondary.bracelet;
      compare += Number(bracelet.price) || 0;
      bundle += Math.max(0, Number(bracelet.price) - (Number(bracelet.discountCents) || 0));
    }

    return {
      compare,
      bundle,
      save: Math.max(0, compare - bundle),
      hasSelection: Boolean(this.selection.primary || this.selection.secondary || this.selection.extra),
    };
  }

  renderPrice({ animate = true } = {}) {
    const { compare, bundle, save, hasSelection } = this.totals();
    const emptyFixedTotal =
      !hasSelection && this.pricingMode === 'fixed_total' && this.fixedTotalCents > 0
        ? this.fixedTotalCents
        : 0;

    this.swapText(
      this.priceBundle,
      hasSelection
        ? this.formatMoney(bundle)
        : emptyFixedTotal
          ? this.formatMoney(emptyFixedTotal)
          : this.config.pricePlaceholder || '—',
      animate,
    );
    if (this.priceCompare) {
      this.priceCompare.textContent = this.formatMoney(compare);
      this.priceCompare.hidden = !hasSelection || save <= 0;
    }
    if (this.priceSave) {
      this.priceSave.textContent = save > 0 ? `Save ${this.formatMoney(save)}` : '';
      this.priceSave.hidden = !hasSelection || save <= 0;
    }
    if (this.priceNote) this.priceNote.hidden = !hasSelection || save <= 0 || !this.config.discountNote;
    this.updateKlarna(hasSelection ? bundle : 0);
  }

  /** Crossfade a value — the real number is never delayed by the animation. */
  swapText(el, value, animate) {
    if (!el || el.textContent === value) return;
    if (!animate || this.prefersReducedMotion()) {
      el.textContent = value;
      return;
    }
    el.classList.add('is-swapping');
    clearTimeout(el._flSwap);
    el.textContent = value;
    el._flSwap = setTimeout(() => el.classList.remove('is-swapping'), 200);
  }

  updateKlarna(amountCents) {
    if (!this.klarnaRoot) return;
    const amount = Math.max(0, Math.round(Number(amountCents) || 0));

    /*
      Klarna rejects a placement amount of 0 ("invalid ... should be a number
      >= 0") and would otherwise advertise four payments of nothing. Keep the
      placement out of the page until there is a real bundle total.
    */
    this.klarnaRoot.hidden = amount <= 0;
    if (amount <= 0) return;

    this.klarnaRoot.dataset.purchaseAmount = String(amount);
    this.klarnaRoot.querySelectorAll('[data-purchase-amount]').forEach((el) => {
      el.setAttribute('data-purchase-amount', String(amount));
    });
    try {
      if (window.Klarna?.OnsiteMessaging?.refresh) {
        window.Klarna.OnsiteMessaging.refresh();
      } else if (typeof window.KlarnaOnsiteService !== 'undefined') {
        window.KlarnaOnsiteService = window.KlarnaOnsiteService || [];
        window.KlarnaOnsiteService.push({ eventName: 'refresh-placements' });
      }
    } catch (err) {
      /* Klarna is optional */
    }
  }

  renderBracelet(bracelet) {
    if (!this.braceletBox) return;
    const valid = bracelet && bracelet.variantId && bracelet.available !== false && Number(bracelet.price) > 0;
    if (!valid) {
      this.braceletBox.hidden = true;
      this.braceletOn = false;
      if (this.braceletToggle) this.braceletToggle.checked = false;
      this.braceletBox.classList.remove('is-active');
      return;
    }
    this.braceletBox.hidden = false;
    const addon = Math.max(0, Number(bracelet.price) - (Number(bracelet.discountCents) || 0));
    if (this.braceletAddon) this.braceletAddon.textContent = `+${this.formatMoney(addon)}`;
    if (this.braceletValue) this.braceletValue.textContent = `(${this.formatMoney(bracelet.price)} value)`;
  }

  /* -------------------------------------------------------------- review state */

  /*
    Review is the confirmation moment, not a second inventory of the set — the
    collapsed accordion headers already list every piece with its price and an
    Edit affordance. This renders the total only.
  */
  renderReview() {
    const { compare, bundle, save, hasSelection } = this.totals();
    const emptyFixedTotal =
      !hasSelection && this.pricingMode === 'fixed_total' && this.fixedTotalCents > 0
        ? this.fixedTotalCents
        : 0;
    if (this.reviewTotal) {
      this.reviewTotal.textContent = this.formatMoney(emptyFixedTotal || bundle);
    }
    if (this.reviewCompare) {
      this.reviewCompare.textContent = this.formatMoney(compare);
      this.reviewCompare.hidden = !hasSelection || save <= 0;
    }
    if (this.reviewSave) {
      this.reviewSave.textContent = save > 0 ? `Save ${this.formatMoney(save)}` : '';
      this.reviewSave.hidden = !hasSelection || save <= 0;
    }
  }

  /* ------------------------------------------------------------------ readiness */

  isComplete() {
    return this.slots
      .filter((slot) => !slot.optional)
      .every((slot) => {
        const item = this.selection[slot.key];
        return Boolean(item?.variantId && item.available !== false);
      });
  }

  syncSupportingBlocks() {
    // Commerce support belongs to the purchase moment. In stepped mode that
    // moment is "the required slots are filled", NOT "the customer reached
    // review" — the last step is optional, so plenty of buyers never get there.
    if (this.priorityBox) this.priorityBox.hidden = this.flowMode === 'steps' && !this.isComplete();
  }

  syncAtc() {
    if (!this.atcBtn) return;
    const ready = this.isComplete();

    // The CTA is always present, in both flows. Hiding it until the review step
    // stranded anyone who left the optional slot alone: the required selections
    // were done, the order was purchasable, and there was no button. Readiness
    // is communicated by the disabled state and the label below instead.
    this.atcBtn.hidden = false;

    if (this.atcState === 'idle' || this.atcState === 'error') {
      this.atcBtn.disabled = !ready;
      this.atcBtn.setAttribute('aria-disabled', String(!ready));
      if (this.atcLabel) {
        this.atcLabel.textContent = ready
          ? this.config.atcIdle || 'Add to bag'
          : this.config.atcIncomplete || 'Complete your selections';
      }
    }
  }

  notifySelection() {
    document.dispatchEvent(
      new CustomEvent('full-look:selection', {
        detail: {
          mode: this.bundleMode,
          slots: {
            primary: this.selection.primary?.productId || null,
            secondary: this.selection.secondary?.productId || null,
            extra: this.selection.extra?.productId || null,
          },
          // Legacy keys retained for anything still listening for Full Look.
          frameProductId: this.selection.primary?.productId || null,
          frameTitle: this.selection.primary?.title || '',
          necklaceProductId: this.selection.secondary?.productId || null,
          necklaceTitle: this.selection.secondary?.title || '',
        },
      }),
    );
  }

  /* ------------------------------------------------------------------- errors */

  showError(message, { slotKey } = {}) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message || '';
    this.errorEl.hidden = !message;
    if (message && slotKey) {
      this.invalidSlots.add(slotKey);
      this.sectionFor(slotKey)?.classList.add('is-invalid');
    }
  }

  clearError() {
    this.showError('');
    this.invalidSlots.forEach((key) => this.sectionFor(key)?.classList.remove('is-invalid'));
    this.invalidSlots.clear();
  }

  setAtcState(state) {
    this.atcState = state;
    if (!this.atcBtn) return;
    this.atcBtn.dataset.flState = state;
    const spinner = this.atcBtn.querySelector('[data-fl-atc-spinner]');

    if (state === 'adding') {
      this.atcBtn.disabled = true;
      this.atcBtn.setAttribute('aria-disabled', 'true');
      this.atcBtn.setAttribute('aria-busy', 'true');
      if (this.atcLabel) this.atcLabel.textContent = this.config.atcAdding || 'Adding your set…';
      spinner?.removeAttribute('hidden');
      return;
    }

    this.atcBtn.removeAttribute('aria-busy');
    spinner?.setAttribute('hidden', '');

    if (state === 'success') {
      this.atcBtn.disabled = true;
      if (this.atcLabel) this.atcLabel.textContent = this.config.atcSuccess || 'Added to bag';
      return;
    }
    this.syncAtc();
  }

  /* -------------------------------------------------------------- add to cart */

  addToCart() {
    if (this._adding) return;
    this.clearError();

    for (const slot of this.slots) {
      if (slot.optional) continue;
      const item = this.selection[slot.key];
      if (!item?.variantId) {
        this.showError(slot.missing || `Choose a ${slot.label.toLowerCase()}.`, { slotKey: slot.key });
        this.openModal(slot.modal, this.sectionFor(slot.key)?.querySelector('[data-fl-choose]'));
        return;
      }
      if (item.available === false) {
        this.showError(
          `${item.title || slot.label} is no longer available. Choose another ${slot.label.toLowerCase()}.`,
          { slotKey: slot.key },
        );
        this.openModal(slot.modal, this.sectionFor(slot.key)?.querySelector('[data-fl-choose]'));
        return;
      }
    }

    const primaryVariantId = String(this.selection.primary.variantId);
    const secondaryVariantId = String(this.selection.secondary.variantId);
    const items = [{ id: parseInt(primaryVariantId, 10), quantity: 1 }];
    const analytics = window.CaffeineOfferAnalytics;
    const offerType = this.bundleMode === 'chain_pendant' ? 'chain_pendant' : 'full_look';
    const offerInstanceId = window.crypto?.randomUUID
      ? window.crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    const analyticsFields = {
      ...(analytics?.fieldsFromElement(this) || {}),
      offerType,
      offerInstanceId,
      role: 'set',
      detail: {
        flow: this.flowMode,
        extra: Boolean(this.selection.extra?.variantId),
        bracelet: Boolean(this.braceletOn)
      }
    };
    analytics?.track('click', { ...analyticsFields, detail: { action: 'add_set', flow: this.flowMode } });
    analytics?.track('add_attempt', analyticsFields);
    const commonProperties = (role) => ({
      _caffeine_offer_type: offerType,
      _caffeine_offer: this.dataset.caffeineOfferId || offerType,
      _caffeine_offer_revision: this.dataset.caffeineOfferRevision || '1',
      _caffeine_instance: offerInstanceId,
      _caffeine_role: role,
      _caffeine_placement: 'pdp',
      _caffeine_sid: analytics?.sessionId || ''
    });

    if (this.bundleMode === 'chain_pendant') {
      // The marker is a fixed string, not the display name: the checkout Function
      // and the analytics grouping both key off it, so renaming the page in the
      // Theme Editor must not silently stop discounts or split reporting.
      //
      // Every piece carries the marker so all of them are attributable in
      // reporting. Only the companions carry _added_with_variant — that absence
      // is what identifies the base chain to the Function.
      items[0].properties = { _bundle: 'Chain & Pendant', ...commonProperties('primary') };
      const link = {
        _bundle: 'Chain & Pendant',
        _added_with_variant: primaryVariantId,
        ...commonProperties('addon')
      };
      items.push({ id: parseInt(secondaryVariantId, 10), quantity: 1, properties: { ...link } });
      if (this.selection.extra?.variantId) {
        items.push({ id: parseInt(this.selection.extra.variantId, 10), quantity: 1, properties: { ...link } });
      }
    } else {
      items[0].properties = commonProperties('primary');
      items.push({
        id: parseInt(secondaryVariantId, 10),
        quantity: 1,
        properties: {
          _bundle: 'The Full Look',
          _full_look_with: primaryVariantId,
          _added_with_variant: primaryVariantId,
          ...commonProperties('addon'),
        },
      });
      if (this.braceletOn && this.selection.secondary.bracelet?.variantId) {
        items.push({
          id: parseInt(this.selection.secondary.bracelet.variantId, 10),
          quantity: 1,
          properties: {
            _bundle: 'Complete the Set',
            _added_with_variant: secondaryVariantId,
            ...commonProperties('addon')
          },
        });
      }
    }

    if (this.priorityCheckbox?.checked) {
      const priorityVariantId = this.priorityCheckbox.getAttribute('data-priority-variant-id');
      if (priorityVariantId) {
        items.push({
          id: parseInt(priorityVariantId, 10),
          quantity: 1,
          properties: { _added_with_variant: primaryVariantId, ...commonProperties('addon') },
        });
      }
    }

    const cartUrl = window.routes?.cart_url || '/cart';
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const sectionIds =
      cart && typeof cart.getSectionsToRender === 'function' ? cart.getSectionsToRender().map((s) => s.id) : [];

    this._adding = true;
    this.setAtcState('adding');

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items, sections: sectionIds, sections_url: window.location.pathname }),
    })
      .then(async (response) => {
        const data = await response.json().catch(() => null);
        if (!response.ok) throw Object.assign(new Error('add failed'), { data });
        return data;
      })
      .then((response) => {
        analytics?.track('conversion', analyticsFields);
        if (cart && typeof cart.renderContents === 'function' && response?.sections) {
          this.setAtcState('success');
          this.announce(this.config.atcSuccess || 'Added to bag');
          // renderContents opens the drawer itself; hand the button back after.
          cart.renderContents(response);
          cart.classList.remove('is-empty');
          setTimeout(() => {
            this._adding = false;
            this.setAtcState('idle');
          }, 900);
        } else {
          window.location.href = cartUrl;
        }
      })
      .catch((error) => {
        analytics?.track('add_error', {
          ...analyticsFields,
          detail: { message: String(error?.data?.description || error?.message || 'add failed').slice(0, 200) }
        });
        this._adding = false;
        this.setAtcState('error');
        const described = error?.data?.description || error?.data?.message;
        this.showError(described || 'We could not add this set. Please try again.');
      });
  }
}

if (!customElements.get('full-look-bundle')) {
  customElements.define('full-look-bundle', FullLookBundle);
}
